/**
 * Free mixer promo (theme settings > Free mixer promo, markup in snippets/free-mixer-popup.liquid).
 *
 * - When a promo product is added to the cart (product page, quick view or product card), the theme's "product:added"
 *   event is held back, the popup lets the customer choose the free mixers, then the event is released so the
 *   mini-cart opens with everything in it.
 * - The "choose your free mixers" button rendered by snippets/free-mixer-cart-state.liquid re-opens the popup.
 * - When the cart holds more gift lines than the promo bottles allow (a bottle was removed), the extra gift lines
 *   are removed.
 *
 * Gift lines carry the hidden "_free_mixer" line item property. Pricing is done by the Shopify "Buy X get Y" discount.
 */
(function () {
  var configElement = document.getElementById("free-mixer-config");
  var modal = document.getElementById("free-mixer-modal");

  if (!configElement || !modal) {
    return;
  }

  var config = JSON.parse(configElement.textContent);
  var GIFT_PROPERTY = "_free_mixer";

  var dialog = modal.querySelector('[role="dialog"]');
  var options = Array.prototype.slice.call(
    modal.querySelectorAll("[data-free-mixer-option]")
  );
  var counterElement = modal.querySelector("[data-free-mixer-counter]");
  var noteElement = modal.querySelector("[data-free-mixer-note]");
  var errorElement = modal.querySelector("[data-free-mixer-error]");
  var hintElement = modal.querySelector("[data-free-mixer-hint]");
  var submitButton = modal.querySelector("[data-free-mixer-submit]");

  var state = {
    isOpen: false,
    isSubmitting: false,
    isSyncing: false,
    total: 0,
    alreadyInCart: 0,
    heldAddedDetail: null,
    previousFocus: null,
  };

  /* ---------------------------------------------------------------------------------------------------------------
   * Cart helpers
   * --------------------------------------------------------------------------------------------------------------- */

  function cartRequest(url, body) {
    return fetch(url, {
      method: body ? "POST" : "GET",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        "X-Requested-With": "XMLHttpRequest",
      },
      body: body ? JSON.stringify(body) : undefined,
    }).then(function (response) {
      return response.json().then(function (content) {
        if (!response.ok) {
          throw content;
        }

        return content;
      });
    });
  }

  function fetchCart() {
    return cartRequest(config.routes.cart + ".js");
  }

  function isGiftLine(item) {
    return !!(item.properties && item.properties[GIFT_PROPERTY]);
  }

  // Mirrors the counting in snippets/free-mixer-cart-state.liquid
  function countCart(cart) {
    var counts = { promo: 0, gifts: 0, otherMixers: 0 };

    cart.items.forEach(function (item) {
      if (isGiftLine(item)) {
        counts.gifts += item.quantity;
      } else if (config.promoProductIds.indexOf(item.product_id) !== -1) {
        counts.promo += item.quantity;
      } else if (config.mixerProductIds.indexOf(item.product_id) !== -1) {
        counts.otherMixers += item.quantity;
      }
    });

    counts.allowed = counts.promo * config.quantityPerBottle;
    counts.remaining = counts.allowed - counts.gifts - counts.otherMixers;

    return counts;
  }

  function dispatchProductAdded(detail) {
    document.documentElement.dispatchEvent(
      new CustomEvent("product:added", { bubbles: true, detail: detail })
    );
  }

  /* ---------------------------------------------------------------------------------------------------------------
   * Hold back "product:added" for promo products
   * --------------------------------------------------------------------------------------------------------------- */

  function getAddedVariantId(detail) {
    if (detail.variant && detail.variant.id) {
      return parseInt(detail.variant.id, 10);
    }

    // Product cards send the clicked button instead of the variant
    var form = detail.button && detail.button.closest("form");
    var idInput = form && form.querySelector('[name="id"]');

    return idInput ? parseInt(idInput.value, 10) : null;
  }

  // Lets the held "product:added" reach the theme cart, which re-renders and opens the mini-cart
  function releaseHeldAdd(addedGiftQuantity) {
    var detail = state.heldAddedDetail;
    state.heldAddedDetail = null;

    if (!detail) {
      return false;
    }

    dispatchProductAdded(
      Object.assign({}, detail, {
        quantity: (detail.quantity || 0) + addedGiftQuantity,
        freeMixerHandled: true,
      })
    );

    return true;
  }

  // Capture phase on window runs before the theme cart listener (bubble phase on <html>)
  window.addEventListener(
    "product:added",
    function (event) {
      var detail = event.detail || {};

      if (detail.freeMixerHandled || !config.hasAvailableMixers) {
        return;
      }

      var variantId = getAddedVariantId(detail);

      if (!variantId || config.promoVariantIds.indexOf(variantId) === -1) {
        return;
      }

      event.stopPropagation();
      state.heldAddedDetail = detail;

      fetchCart()
        .then(function (cart) {
          var counts = countCart(cart);

          if (counts.remaining > 0) {
            openModal(counts);
          } else {
            releaseHeldAdd(0);
          }
        })
        .catch(function () {
          releaseHeldAdd(0);
        });
    },
    true
  );

  /* ---------------------------------------------------------------------------------------------------------------
   * "Choose your free mixers" button in the mini-cart / cart page
   * --------------------------------------------------------------------------------------------------------------- */

  document.addEventListener("click", function (event) {
    var trigger = event.target.closest("[data-free-mixer-open]");

    if (!trigger) {
      return;
    }

    event.preventDefault();
    trigger.setAttribute("disabled", "disabled");

    fetchCart()
      .then(function (cart) {
        var counts = countCart(cart);

        if (counts.remaining > 0) {
          openModal(counts);
        }
      })
      .finally(function () {
        trigger.removeAttribute("disabled");
      });
  });

  /* ---------------------------------------------------------------------------------------------------------------
   * Popup
   * --------------------------------------------------------------------------------------------------------------- */

  function getQuantity(option) {
    return parseInt(option.getAttribute("data-quantity") || "0", 10);
  }

  function getSelectedTotal() {
    return options.reduce(function (sum, option) {
      return sum + getQuantity(option);
    }, 0);
  }

  function setQuantity(option, quantity) {
    option.setAttribute("data-quantity", quantity);
    option.querySelector("[data-free-mixer-quantity]").textContent = quantity;
    option.classList.toggle("is-selected", quantity > 0);
  }

  function render() {
    var selected = getSelectedTotal();
    var remainingToPick = state.total - selected;

    options.forEach(function (option) {
      var quantity = getQuantity(option);
      var max = parseInt(option.getAttribute("data-max"), 10);

      option.querySelector('[data-free-mixer-step="-1"]').disabled =
        quantity <= 0;
      option.querySelector('[data-free-mixer-step="1"]').disabled =
        remainingToPick <= 0 || quantity >= max;
    });

    counterElement.textContent = config.strings.counter
      .replace("[selected]", selected)
      .replace("[total]", state.total);

    // Shopify's "Buy X get Y" discount only makes mixers free in complete sets (e.g. 3 per bottle) and counts every
    // eligible mixer in the cart. Customers may take fewer sets than allowed, but never a partial set they would pay for.
    var setSize = config.quantityPerBottle;
    var missingForSet = (setSize - ((state.alreadyInCart + selected) % setSize)) % setSize;

    if (selected > 0 && missingForSet > 0) {
      hintElement.textContent = config.strings.setHint
        .replace("[size]", setSize)
        .replace("[count]", missingForSet);
      hintElement.hidden = false;
    } else {
      hintElement.hidden = true;
    }

    submitButton.disabled =
      state.isSubmitting || selected < 1 || selected > state.total || missingForSet > 0;
  }

  function showError(message) {
    errorElement.textContent = message || config.strings.error;
    errorElement.hidden = false;
  }

  function openModal(counts) {
    state.total = counts.remaining;
    state.isOpen = true;
    state.previousFocus = document.activeElement;

    options.forEach(function (option) {
      setQuantity(option, 0);
    });

    // The limit covers the whole cart (all promo bottles minus mixers already there): explain it when it differs
    // from a single bottle's allowance
    var alreadyInCart = counts.gifts + counts.otherMixers;
    state.alreadyInCart = alreadyInCart;

    if (counts.promo > 1 || alreadyInCart > 0) {
      noteElement.textContent = config.strings.summary
        .replace("[bottles]", counts.promo)
        .replace("[allowed]", counts.allowed)
        .replace("[chosen]", alreadyInCart);
      noteElement.hidden = false;
    } else {
      noteElement.hidden = true;
    }

    errorElement.hidden = true;
    render();

    document.dispatchEvent(new CustomEvent("theme:loading:end"));
    document.documentElement.classList.add("free-mixer-is-open");
    modal.setAttribute("aria-hidden", "false");
    dialog.focus();
  }

  function closeModal(addedGiftQuantity) {
    if (!state.isOpen) {
      return;
    }

    state.isOpen = false;
    modal.setAttribute("aria-hidden", "true");
    document.documentElement.classList.remove("free-mixer-is-open");

    // Opened after an add to cart: let the theme open the mini-cart now. Opened from the cart: refresh it.
    if (!releaseHeldAdd(addedGiftQuantity) && addedGiftQuantity > 0) {
      dispatchProductAdded({ quantity: addedGiftQuantity, freeMixerHandled: true });
    }

    if (state.previousFocus && state.previousFocus.focus) {
      state.previousFocus.focus();
    }
  }

  function submit() {
    var items = options
      .filter(function (option) {
        return getQuantity(option) > 0;
      })
      .map(function (option) {
        return {
          id: parseInt(option.getAttribute("data-variant-id"), 10),
          quantity: getQuantity(option),
          properties: { _free_mixer: "1" },
        };
      });

    if (items.length === 0) {
      return;
    }

    state.isSubmitting = true;
    errorElement.hidden = true;
    render();
    document.dispatchEvent(new CustomEvent("theme:loading:start"));

    cartRequest(config.routes.cartAdd + ".js", { items: items })
      .then(function () {
        state.isSubmitting = false;
        closeModal(getSelectedTotal());
      })
      .catch(function (error) {
        state.isSubmitting = false;
        document.dispatchEvent(new CustomEvent("theme:loading:end"));
        showError(error && error.description);
        render();
      });
  }

  modal.addEventListener("click", function (event) {
    // Keep clicks inside the popup from reaching the theme (the mini-cart closes on outside clicks)
    event.stopPropagation();

    var stepButton = event.target.closest("[data-free-mixer-step]");

    if (stepButton) {
      var option = stepButton.closest("[data-free-mixer-option]");
      var step = parseInt(stepButton.getAttribute("data-free-mixer-step"), 10);

      setQuantity(option, Math.max(0, getQuantity(option) + step));
      render();
      return;
    }

    if (event.target.closest("[data-free-mixer-submit]")) {
      submit();
      return;
    }

    if (event.target.closest("[data-free-mixer-close]") && !state.isSubmitting) {
      closeModal(0);
    }
  });

  // The open mini-cart traps focus through a document "focusin" listener: keep focus inside the popup
  modal.addEventListener("focusin", function (event) {
    event.stopPropagation();
  });

  document.addEventListener("keydown", function (event) {
    if (state.isOpen && event.key === "Escape" && !state.isSubmitting) {
      event.stopPropagation();
      closeModal(0);
    }
  });

  /* ---------------------------------------------------------------------------------------------------------------
   * Remove extra gift lines (more gifts than the promo bottles allow)
   * --------------------------------------------------------------------------------------------------------------- */

  function syncExcessGifts() {
    var scope =
      window.theme && window.theme.pageType === "cart"
        ? document.querySelector('[data-section-type="cart"]')
        : document.getElementById("mini-cart");

    if (state.isSyncing || !scope || !scope.querySelector("[data-free-mixer-excess]")) {
      return;
    }

    state.isSyncing = true;

    fetchCart()
      .then(function (cart) {
        var counts = countCart(cart);
        var excess = counts.gifts - counts.allowed;
        var updates = {};

        // Trim the most recently added gift lines first
        cart.items
          .slice()
          .reverse()
          .forEach(function (item) {
            if (excess <= 0 || !isGiftLine(item)) {
              return;
            }

            var removed = Math.min(item.quantity, excess);
            updates[item.key] = item.quantity - removed;
            excess -= removed;
          });

        if (Object.keys(updates).length === 0) {
          return false;
        }

        return cartRequest(config.routes.cartUpdate + ".js", { updates: updates }).then(function () {
          return true;
        });
      })
      .then(function (changed) {
        state.isSyncing = false;

        if (changed) {
          document.documentElement.dispatchEvent(
            new CustomEvent("cart:refresh", { bubbles: true, detail: { scrollToTop: false } })
          );
        }
      })
      .catch(function () {
        state.isSyncing = false;
      });
  }

  // "cart:rerendered" does not bubble from the mini-cart, so listen in the capture phase
  document.addEventListener("cart:rerendered", syncExcessGifts, true);
  syncExcessGifts();
})();
